import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, CheckCircle2 } from 'lucide-react';
import { Badge } from '../../../components/ui/index.js';
import { useDaemonScope } from './DaemonScopeContext.js';
import { daemonReads } from './daemon-queries.js';
import {
  RunDecision,
  RunReport,
  STATUS_VARIANTS,
  asCount,
  asKey,
  asText,
  unwrapListResponse,
} from './run-audit-shared.js';
import { ReportAuditDetails } from './RunAuditTabs.js';

export const RunArtifactsPanel: React.FC<{ daemonId: number; runId: number }> = ({ daemonId, runId }) => {
  const { scope } = useDaemonScope();
  const reads = daemonReads(scope);

  const reportsQuery = useQuery(reads.runReports(daemonId, runId));
  const decisionsQuery = useQuery(reads.runDecisions(daemonId, runId));

  const reports = unwrapListResponse<RunReport>(reportsQuery.data);
  const decisions = unwrapListResponse<RunDecision>(decisionsQuery.data);
  const isLoading = reportsQuery.isLoading || decisionsQuery.isLoading;

  if (isLoading) {
    return <div className="text-xs text-muted">Loading run artifacts...</div>;
  }

  if (reportsQuery.isError || decisionsQuery.isError) {
    return <div className="text-xs text-danger">Could not load run artifacts.</div>;
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
      <div className="rounded-sm border bg-surface p-3">
        <div className="flex items-center gap-2 text-sm font-medium text-body mb-2">
          <FileText size={14} className="text-accent-text" />
          Reports
          <Badge variant="neutral" size="sm">
            {reports.length}
          </Badge>
        </div>
        {reports.length === 0 ? (
          <div className="text-xs text-muted">No reports emitted.</div>
        ) : (
          <div className="space-y-2">
            {reports.map((report) => (
              <div key={report.id} className="rounded-sm border bg-surface-alt p-2">
                <div className="text-sm font-medium text-body">{report.title ?? `Report ${report.id}`}</div>
                {report.summary && <div className="text-xs text-muted mt-1">{report.summary}</div>}
                {report.metrics && (
                  <div className="mt-2 text-xs text-muted">
                    Commands: {asCount(report.metrics.commands)} / Failures: {asCount(report.metrics.failures)}
                  </div>
                )}
                <ReportAuditDetails report={report} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-sm border bg-surface p-3">
        <div className="flex items-center gap-2 text-sm font-medium text-body mb-2">
          <CheckCircle2 size={14} className="text-accent-text" />
          Decisions
          <Badge variant="neutral" size="sm">
            {decisions.length}
          </Badge>
        </div>
        {decisions.length === 0 ? (
          <div className="text-xs text-muted">No decisions requested.</div>
        ) : (
          <div className="space-y-2">
            {decisions.map((decision) => (
              <div key={decision.id} className="rounded-sm border bg-surface-alt p-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium text-body">{decision.title ?? `Decision ${decision.id}`}</div>
                  <Badge variant={STATUS_VARIANTS[asKey(decision.status)] ?? 'warning'} size="sm">
                    {asText(decision.status) || 'pending'}
                  </Badge>
                </div>
                {decision.description ? (
                  <div className="text-xs text-muted mt-1">{asText(decision.description)}</div>
                ) : null}
                {decision.recommendation && (
                  <div className="text-xs text-body mt-2">Recommendation: {decision.recommendation}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
